import { Box, Text } from "@chakra-ui/react-v3";
import { buildDayjs } from "@metriport/shared/common/date";
import { ReactNode } from "react";

export type ResourceTimelineItem = {
  date: string;
  content: ReactNode;
};

export type ResourceTimelineLayoutProps = {
  title?: string;
  items: ResourceTimelineItem[];
};

/**
 * Container component for timeline-based resource displays.
 * Provides scrollable container with fade gradient, year dividers, and sticky headers.
 * Takes an explicit array of items with dates for proper year grouping.
 */
export function ResourceTimelineLayout({ title, items }: ResourceTimelineLayoutProps) {
  if (items.length === 0) {
    return null;
  }

  // Process items with year information for divider logic
  const itemsWithYears = items.map((item, index) => ({
    ...item,
    year: buildDayjs(item.date).year(),
    index,
  }));

  return (
    <Box>
      {title && (
        <Text fontSize="lg" fontWeight="semibold" mb={3}>
          {title}
        </Text>
      )}
      <Box
        position="relative"
        _after={{
          content: '""',
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: "60px",
          background: "linear-gradient(to top, var(--chakra-colors-bg), transparent)",
          pointerEvents: "none",
        }}
      >
        <Box
          maxH="400px"
          overflowY="auto"
          pr={2}
          pb="30px"
          css={{
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "var(--chakra-colors-bg-subtle)",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "var(--chakra-colors-border-emphasized)",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "var(--chakra-colors-fg-muted)",
            },
          }}
        >
          {itemsWithYears.map((item, index) => {
            const currentYear = item.year;

            const previousItem = index > 0 ? itemsWithYears[index - 1] : undefined;
            const previousYear = previousItem?.year;

            const showYearDivider =
              currentYear && (index === 0 || (previousYear && currentYear !== previousYear));

            return (
              <Box key={item.index} mb={3}>
                {showYearDivider && (
                  <Box
                    position="sticky"
                    top={0}
                    zIndex={10}
                    bg="bg"
                    mb={2}
                    borderTop="2px solid"
                    borderColor="border.emphasized"
                  >
                    <Text fontSize="sm" fontWeight="semibold" color="fg.muted" mt={1.5} mb={1}>
                      {currentYear}
                    </Text>
                  </Box>
                )}
                {item.content}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
